import { useEffect, type ReactNode } from 'react';

import { Icon, type IconName } from '../Icon';
import { messageCloseIconName, messageIconNames } from './icons';
import styles from './Message.module.css';

export type MessageType = 'primary' | 'success' | 'warning' | 'danger' | 'info';

const statusText: Record<MessageType, string> = {
  danger: '失败',
  info: '提示',
  primary: '提示',
  success: '成功',
  warning: '警告',
};

export interface MessageProps {
  className?: string;
  align?: 'left' | 'center';
  duration?: number;
  message: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  type?: MessageType;
}

export interface MessageStackProps {
  children: ReactNode;
  className?: string;
}

export function Message({ align = 'left', className, duration = 3000, message, onOpenChange, open, type = 'info' }: MessageProps) {
  useEffect(() => {
    if (!open || duration <= 0) return;
    const timer = window.setTimeout(() => onOpenChange(false), duration);
    return () => window.clearTimeout(timer);
  }, [duration, onOpenChange, open]);

  if (!open) return null;

  const classes = [styles.root, styles[type], className].filter(Boolean).join(' ');
  const isError = type === 'danger';

  return <div aria-live={isError ? 'assertive' : 'polite'} className={classes} data-align={align} data-type={type} role={isError ? 'alert' : 'status'}>
    <span className={styles.content}>
      <Icon className={styles.icon} data-icon={messageIconNames[type]} name={messageIconNames[type] as IconName} size="base" />
      <span className={styles.visuallyHidden}>{statusText[type]}</span>
      <span className={styles.text}>{message}</span>
    </span>
    <button aria-label="关闭提示" className={styles.close} data-icon={messageCloseIconName} onClick={() => onOpenChange(false)} type="button">
      <Icon name="close" size="xs" />
    </button>
  </div>;
}

export function MessageStack({ children, className }: MessageStackProps) {
  return <section aria-label="系统提示" className={[styles.stack, className].filter(Boolean).join(' ')}>{children}</section>;
}
